import { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Stethoscope, Shield, Truck, Plane, PlaneTakeoff, Train, Coffee, User, Sparkles, ArrowRight, Layers, Check } from "lucide-react";
import { toast } from "sonner";

const PERSONAS = [
  { key: "doctor", label: "Doctor", icon: Stethoscope, preset: "doctor_on_call" },
  { key: "nurse", label: "Nurse", icon: Stethoscope, preset: "nurse_3x12" },
  { key: "pilot", label: "Pilot", icon: PlaneTakeoff, preset: "pilot_4on3off" },
  { key: "steward", label: "Cabin Crew", icon: Coffee, preset: null },
  { key: "train_driver", label: "Train Driver", icon: Train, preset: "train_early_late" },
  { key: "security", label: "Security", icon: Shield, preset: "security_night_week" },
  { key: "courier", label: "Courier", icon: Truck, preset: "courier_split" },
  { key: "traveler", label: "Traveler", icon: Plane, preset: null },
  { key: "shift_worker", label: "Other", icon: User, preset: null },
];

const STORAGE_KEY = "circashift.onboarded";

function todayIso() {
  const d = new Date();
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function OnboardingSplash({ onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [persona, setPersona] = useState(user?.persona || "doctor");
  const [applyPreset, setApplyPreset] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    const key = `${STORAGE_KEY}.${user.id}`;
    if (localStorage.getItem(key)) return;
    const t = setTimeout(() => setOpen(true), 500);
    return () => clearTimeout(t);
  }, [user]);

  const chosen = PERSONAS.find((p) => p.key === persona);

  const finish = async () => {
    setBusy(true);
    try {
      if (chosen?.preset && applyPreset) {
        await http.post("/shifts/preset", {
          preset_id: chosen.preset,
          start_date: todayIso(),
          cycles: 2,
        });
        toast.success("Rota ready", { description: `Applied ${chosen.label} preset for 2 cycles` });
      }
      localStorage.setItem(`${STORAGE_KEY}.${user.id}`, new Date().toISOString());
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply preset");
    } finally { setBusy(false); }
  };

  const skip = () => {
    localStorage.setItem(`${STORAGE_KEY}.${user.id}`, "skipped");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) skip(); }}>
      <DialogContent className="bg-[#0F141F] border-white/10 text-slate-100 sm:max-w-md rounded-2xl">
        <DialogHeader>
          <div className="pill mx-auto mb-3 bg-indigo-950/60 border-indigo-500/40 text-indigo-200 w-fit">
            <Sparkles className="w-3.5 h-3.5" /> Welcome
          </div>
          <DialogTitle className="font-display text-2xl font-extrabold text-center">
            {step === 0 ? "Which role fits you best?" : "Start with a rota preset?"}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm text-center">
            {step === 0
              ? "We'll tailor recommendations, alerts, and rota presets to your shift pattern."
              : `We can pre-fill 2 cycles of the ${chosen?.label} preset so your timeline is populated from day one.`}
          </DialogDescription>
        </DialogHeader>

        {step === 0 && (
          <>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {PERSONAS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPersona(p.key)}
                  data-testid={`onboarding-persona-${p.key}`}
                  className={`flex flex-col items-center justify-center gap-1 rounded-xl border p-3 transition ${
                    persona === p.key ? "border-indigo-400 bg-indigo-500/10 text-indigo-200" : "border-white/10 text-slate-400 hover:bg-white/5"
                  }`}
                >
                  <p.icon className="w-5 h-5" />
                  <span className="text-[10px] uppercase tracking-widest font-mono-ui text-center leading-tight">{p.label}</span>
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={skip} className="rounded-full text-slate-400 hover:text-slate-200 hover:bg-white/5">Skip</Button>
              <Button
                onClick={() => setStep(1)}
                data-testid="onboarding-continue"
                className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
              >
                Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            {chosen?.preset ? (
              <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-4 mt-2">
                <div className="flex items-center gap-2 mb-2">
                  <Layers className="w-4 h-4 text-indigo-300" />
                  <div className="font-display font-semibold">Suggested preset</div>
                </div>
                <div className="text-slate-200 text-sm">{chosen.label} rota</div>
                <label className="mt-3 flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={applyPreset}
                    onChange={(e) => setApplyPreset(e.target.checked)}
                    data-testid="onboarding-apply-preset"
                    className="mt-1 accent-indigo-500"
                  />
                  <span className="text-xs text-slate-300 leading-relaxed">
                    Apply 2 cycles starting today. You can edit or delete any shift from the calendar.
                  </span>
                </label>
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-4 mt-2 text-sm text-slate-400">
                No pre-built preset for this role yet — add your first shift from the calendar. Everything else works the same.
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 mt-4">
              <Button variant="outline" onClick={() => setStep(0)} className="rounded-full bg-transparent border-white/15 text-slate-200 hover:bg-white/5">
                Back
              </Button>
              <Button
                onClick={finish}
                disabled={busy}
                data-testid="onboarding-finish"
                className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
              >
                <Check className="w-4 h-4 mr-1" /> {busy ? "Setting up…" : "I'm ready"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
